/**
 * GadsExport.js — Self-Service-Export für EINE Kampagne (oben gewählt): Wochen × Berichte (b01–b12c) → ZIP.
 * Die Kampagnen-Auswahl ganz oben gilt für ALLES: Berichte, Fragen (inkl. b13/b14), Was-wurde-gemacht.
 * Backend: api/gads_export_bxx.php (ZIP) + gads_kpi_questions.php (Fragen) + gads_kpi_changelog.php (Log). Bearer.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './GadsKpi.css';

const API_BASE = '/api';
const KAMPS = [['VA', 'Verlustangst'], ['EF', 'Eifersucht'], ['EA', 'Emot. Abh.'], ['SO', 'Souveränität'], ['BR', 'Branding']];
const REPORTS = [
  ['b01', 'Kampagne'], ['b02', 'Auktionen'], ['b03', 'Geräte'], ['b04', 'Alter'], ['b05', 'Standorte'],
  ['b06', 'Werbezeitplan'], ['b07', 'Zielgruppen'], ['b08', 'Suchbegriffe'], ['b09', 'Keywords'],
  ['b10', 'Titel'], ['b11', 'Texte'], ['b12', 'Sitelinks'], ['b12a', 'Marke'], ['b12c', 'Bilder'],
];
// Fragen-Liste = Daten-Berichte + Synthese-Fragen (b13/b14, keine Daten-Tabelle, nur Fragen)
const FRAGEN = [...REPORTS, ['b13', 'Zusammenfassung: K + LP → mehr Conversions'], ['b14', 'User-Intent / eF-Profil']];

export default function GadsExport() {
  const [kCamp, setKCamp] = useState('VA'); // EINE Kampagne, gilt für die ganze Seite
  const [weeks, setWeeks] = useState(null);
  const [selWeeks, setSelWeeks] = useState({});
  const [selR, setSelR] = useState(Object.fromEntries(REPORTS.map(r => [r[0], true])));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  // Fragen
  const [frText, setFrText] = useState({});
  const [selFr, setSelFr] = useState(Object.fromEntries(FRAGEN.map(r => [r[0], true])));
  const [frBusy, setFrBusy] = useState(false);
  const [frMsg, setFrMsg] = useState('');
  const [bigTa, setBigTa] = useState({}); // bxx → vergrößert (Doppelklick)
  // Was wurde gemacht (K × Woche)
  const [clWeek, setClWeek] = useState('');
  const [clData, setClData] = useState({ lp_name: '', k_gads: [], k_manual: '', lp: '', source: 'auto' });
  const [clBusy, setClBusy] = useState(false);
  const [clMsg, setClMsg] = useState('');
  const [inclCl, setInclCl] = useState(true); // WasWurdeGemacht.csv ins ZIP
  const [inclJourney, setInclJourney] = useState(true); // Journey+Profil ins ZIP (LP-Wirkung)
  const [clBig, setClBig] = useState({}); // 'km' | 'lp' → vergrößert
  // Änderungsverlauf-Import (CSV-Datei wählen → Vorschau → Speichern)
  const [impOpen, setImpOpen] = useState(false);
  const [impRaw, setImpRaw] = useState('');
  const [impFile, setImpFile] = useState('');
  const [impPrev, setImpPrev] = useState(null);
  const [impBusy, setImpBusy] = useState(false);
  const [impMsg, setImpMsg] = useState('');

  const token = () => localStorage.getItem('token') || '';

  useEffect(() => {
    fetch(`${API_BASE}/gads_export_bxx.php?list=weeks`, { headers: { Authorization: 'Bearer ' + token() } })
      .then(r => r.json())
      .then(j => { if (j.success) { setWeeks(j.weeks); setSelWeeks(Object.fromEntries(j.weeks.slice(0, 2).map(w => [w.from, true]))); setClWeek(j.weeks[0]?.from || ''); } else setErr(j.message || 'Fehler'); })
      .catch(e => setErr(String(e)));
  }, []);

  const loadChangelog = useCallback((camp, week) => {
    if (!week) return;
    setClMsg('');
    fetch(`${API_BASE}/gads_kpi_changelog.php?campaign=${camp}&week=${week}`, { headers: { Authorization: 'Bearer ' + token() } })
      .then(r => r.json())
      .then(j => { if (j.success) setClData({ lp_name: j.lp_name || '', k_gads: j.k_gads || [], k_manual: j.k_manual || '', lp: j.lp || '', source: j.source || 'auto' }); })
      .catch(() => {});
  }, []);
  useEffect(() => { loadChangelog(kCamp, clWeek); }, [kCamp, clWeek, loadChangelog]);

  const impPreview = async (raw) => {
    const csv = (raw != null ? raw : impRaw);
    if (!csv.trim()) return;
    setImpBusy(true); setImpMsg(''); setImpPrev(null);
    try {
      const res = await fetch(`${API_BASE}/gads_kpi_changelog.php`, {
        method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'preview', campaign: kCamp, csv }),
      });
      const j = await res.json();
      if (j.success) { setImpPrev(j); if (!j.groups.length) setImpMsg('Keine Änderungen erkannt — ist es der GADS-Änderungsverlauf-CSV-Export?'); }
      else setImpMsg('Fehler: ' + (j.message || ''));
    } catch (e) { setImpMsg('Fehler: ' + e); }
    setImpBusy(false);
  };

  const handleImpFile = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = ''; // erlaubt erneutes Wählen derselben Datei
    if (!f) return;
    setImpFile(f.name); setImpMsg(''); setImpPrev(null);
    const reader = new FileReader();
    reader.onload = () => { const text = String(reader.result || ''); setImpRaw(text); impPreview(text); };
    reader.onerror = () => setImpMsg('Datei konnte nicht gelesen werden.');
    reader.readAsText(f);
  };

  const impSave = async () => {
    if (!impPrev || !impPrev.groups.length) return;
    setImpBusy(true); setImpMsg('');
    try {
      const res = await fetch(`${API_BASE}/gads_kpi_changelog.php`, {
        method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'import', campaign: kCamp, csv: impRaw }),
      });
      const j = await res.json();
      if (j.success) {
        setImpMsg(`✔ ${j.imported} Einträge importiert: ${(j.weeks || []).join(' · ')}`);
        setImpRaw(''); setImpFile(''); setImpPrev(null); loadChangelog(kCamp, clWeek);
      } else setImpMsg('Fehler: ' + (j.message || ''));
    } catch (e) { setImpMsg('Fehler: ' + e); }
    setImpBusy(false);
  };

  const saveChangelog = async () => {
    setClBusy(true); setClMsg('');
    try {
      const res = await fetch(`${API_BASE}/gads_kpi_changelog.php`, {
        method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaign: kCamp, week: clWeek, k_manual: clData.k_manual, lp: clData.lp }),
      });
      const j = await res.json();
      setClMsg(j.success ? '✔ gespeichert' : ('Fehler: ' + (j.message || '')));
    } catch (e) { setClMsg('Fehler: ' + e); }
    setClBusy(false);
  };

  const loadQuestions = useCallback((camp) => {
    setFrMsg('');
    fetch(`${API_BASE}/gads_kpi_questions.php?campaign=${camp}`, { headers: { Authorization: 'Bearer ' + token() } })
      .then(r => r.json())
      .then(j => { if (j.success) setFrText(Object.fromEntries(Object.entries(j.questions).map(([k, arr]) => [k, arr.join('\n')]))); })
      .catch(() => {});
  }, []);
  useEffect(() => { loadQuestions(kCamp); }, [kCamp, loadQuestions]);

  const saveQuestions = async () => {
    setFrBusy(true); setFrMsg('');
    try {
      const res = await fetch(`${API_BASE}/gads_kpi_questions.php`, {
        method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaign: kCamp, questions: frText }),
      });
      const j = await res.json();
      setFrMsg(j.success ? `✔ ${kCamp}: ${j.saved} Fragen gespeichert` : ('Fehler: ' + (j.message || '')));
    } catch (e) { setFrMsg('Fehler: ' + e); }
    setFrBusy(false);
  };

  const toggle = (setter) => (key) => setter(s => ({ ...s, [key]: !s[key] }));
  const setAllR = v => setSelR(Object.fromEntries(REPORTS.map(r => [r[0], v])));
  const picks = obj => Object.keys(obj).filter(k => obj[k]);

  const doExport = async () => {
    const wks = picks(selWeeks), reps = picks(selR), frs = picks(selFr);
    if (!reps.length && !frs.length && !inclCl && !inclJourney) { setErr('Mindestens 1 Bericht, Fragen, „Was wurde gemacht" oder Journey wählen.'); return; }
    if ((reps.length || inclCl || inclJourney) && !wks.length) { setErr('Für Berichte / Was-wurde-gemacht / Journey mindestens 1 Woche wählen.'); return; }
    setErr(''); setMsg(''); setBusy(true);
    try {
      const p = new URLSearchParams({ campaigns: kCamp, weeks: wks.join(','), reports: reps.join(','), fragen: frs.join(','), changelog: inclCl ? '1' : '', journey: inclJourney ? '1' : '' });
      const res = await fetch(`${API_BASE}/gads_export_bxx.php?${p}`, { headers: { Authorization: 'Bearer ' + token() } });
      if (!res.ok) { const t = await res.text(); throw new Error('HTTP ' + res.status + ' ' + t.slice(0, 120)); }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const fname = (cd.match(/filename="([^"]+)"/) || [])[1] || 'GAds_Export.zip';
      const nf = res.headers.get('X-Files'), nr = res.headers.get('X-Rows');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = fname;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
      setMsg(`✔ ${fname} — ${nf || '?'} Dateien · ${nr || '?'} Zeilen`);
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  const chk = (on) => 'gk-kchk' + (on ? ' on' : '');
  const kFull = (KAMPS.find(k => k[0] === kCamp) || [kCamp, ''])[1];

  return (
    <div className="gk-wrap">
      <Link to="/admin" className="gk-back">← Zurück zum Dashboard</Link>
      <h2 className="gk-title">⬇ GADS Export <small>eine Kampagne · Wochen · Berichte · Fragen → ZIP (CSV)</small></h2>

      {/* EINE K-Auswahl ganz oben — gilt für alles darunter */}
      <div className="gx-kbar">
        <span className="gx-kbar-lbl">Kampagne</span>
        <select className="gx-select gx-kbar-sel" value={kCamp} onChange={e => setKCamp(e.target.value)}>
          {KAMPS.map(([c, full]) => <option key={c} value={c}>{c} · {full}</option>)}
        </select>
        <span className="gk-toolbar-hint">gilt für Berichte, Fragen und Was-wurde-gemacht</span>
      </div>

      <div className="gx-grid">
        <section className="gk-section">
          <div className="gk-head" style={{ cursor: 'default' }}><span>1 · Berichte</span>
            <span><button className="gk-kbtn" onClick={() => setAllR(true)}>Alle an</button> <button className="gk-kbtn" onClick={() => setAllR(false)}>Alle ab</button></span>
          </div>
          <div className="gx-body">
            {REPORTS.map(([k, lbl]) => (
              <label key={k} className={chk(selR[k])}><input type="checkbox" checked={!!selR[k]} onChange={() => toggle(setSelR)(k)} /><span>{k} <small>{lbl}</small></span></label>
            ))}
          </div>
        </section>

        <section className="gk-section">
          <div className="gk-head" style={{ cursor: 'default' }}><span>2 · Wochen (Fr–Do)</span></div>
          <div className="gx-body gx-weeks">
            {!weeks ? <span className="gk-placeholder">lädt…</span> : weeks.map(w => (
              <label key={w.from} className={chk(selWeeks[w.from])}><input type="checkbox" checked={!!selWeeks[w.from]} onChange={() => toggle(setSelWeeks)(w.from)} /><span>{w.label}</span></label>
            ))}
          </div>
        </section>
      </div>

      <section className="gk-section">
        <div className="gk-head" style={{ cursor: 'default' }}>
          <span>3 · Fragen <small style={{ fontWeight: 400 }}>({kCamp} · {kFull} — für die CD-Auswertung; b13 = K + LP → mehr Conversions)</small></span>
          <span className="gx-frtop">
            <button className="gk-kbtn" onClick={saveQuestions} disabled={frBusy}>{frBusy ? '⏳' : '💾 Fragen speichern'}</button>
            {frMsg && <span className="gx-frmsg">{frMsg}</span>}
          </span>
        </div>
        <div className="gx-frbody">
          <div className="gx-frhint">Häkchen links = diese bxx-Fragen kommen in <code>{kCamp}_Fragen.csv</code>. Eine Frage pro Zeile. <strong>Doppelklick</strong> auf ein Textfeld = vergrößern / wieder verkleinern.</div>
          <div className="gx-frscroll">
            {FRAGEN.map(([k, lbl]) => (
              <div className="gx-frrow" key={k}>
                <label className="gx-frchk"><input type="checkbox" checked={!!selFr[k]} onChange={() => toggle(setSelFr)(k)} /> <strong>{k}</strong> <span className="gx-frlbl">{lbl}</span></label>
                <textarea className={'gx-frta' + (bigTa[k] ? ' gx-frta-big' : '')} rows={bigTa[k] ? 16 : 2}
                  onDoubleClick={() => setBigTa(s => ({ ...s, [k]: !s[k] }))}
                  placeholder="(noch keine Fragen)" value={frText[k] || ''} onChange={e => setFrText(t => ({ ...t, [k]: e.target.value }))} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="gk-section">
        <div className="gk-head" style={{ cursor: 'default' }}>
          <span>4 · Was wurde gemacht <small style={{ fontWeight: 400 }}>({kCamp} × Woche — Kontext für CD: hausgemacht oder Markt?)</small></span>
          <span className="gx-frtop">
            <select className="gx-select" value={clWeek} onChange={e => setClWeek(e.target.value)}>
              {(weeks || []).map(w => <option key={w.from} value={w.from}>{w.label}</option>)}
            </select>
            <button className="gk-kbtn" onClick={saveChangelog} disabled={clBusy}>{clBusy ? '⏳' : '💾 Speichern'}</button>
            {clMsg && <span className="gx-frmsg">{clMsg}</span>}
          </span>
        </div>
        <div className="gx-frbody">
          <div className="gx-frhint">
            <label className="gx-clincl"><input type="checkbox" checked={inclCl} onChange={() => setInclCl(v => !v)} /> <strong>„Was wurde gemacht" als <code>{kCamp}_WasWurdeGemacht.csv</code> ins ZIP</strong> (für die gewählten Wochen)</label>
            <div>GADS-Log: <strong>Import</strong> (Änderungsverlauf-CSV, voller Wortlaut) hat Vorrang, sonst <strong>automatisch</strong> (API, für Anzeigen-Edits ungenau). Manuelle K-/LP-Notizen ergänzen. <strong>Doppelklick</strong> = Textfeld vergrößern.</div>
          </div>
          <div className="gx-clrow">
            <label className="gx-cllbl">
              K-Änderungen · GADS-Log
              <span className={'gx-src ' + (clData.source === 'import' ? 'gx-src-imp' : 'gx-src-auto')}>{clData.source === 'import' ? '● importiert' : '○ automatisch'}</span>
              <small>({clData.k_gads.length})</small>
              <button className="gk-kbtn gx-impbtn" onClick={() => { setImpOpen(o => !o); setImpPrev(null); setImpMsg(''); }}>{impOpen ? '✕ Import schließen' : '⬆ Änderungsverlauf importieren'}</button>
            </label>
            <textarea className="gx-frta gx-clro" readOnly rows={4}
              value={clData.k_gads.map(g => (g.zeit || '').slice(0, 16) + ' · ' + g.text).join('\n')}
              placeholder="(keine Log-Einträge in dieser Woche)" />
          </div>

          {impOpen && (
            <div className="gx-imp">
              <div className="gx-frhint">Google Ads → <strong>Änderungsverlauf</strong> → Zeitraum wählen → <strong>Herunterladen/Exportieren</strong> (CSV) → hier die Datei wählen. Kampagne + Woche werden automatisch erkannt (mehrere Wochen in einer Datei möglich); <strong>Import überschreibt</strong> den Auto-Log für die betroffenen K×Wochen.</div>
              <div className="gx-imp-actions">
                <label className="gk-kbtn gx-filebtn">📄 CSV-Datei wählen…
                  <input type="file" accept=".csv,text/csv,text/plain" onChange={handleImpFile} />
                </label>
                {impFile && <span className="gx-frlbl">{impFile}</span>}
                {impBusy && !impPrev && <span className="gx-frmsg">⏳ prüfe…</span>}
                {impPrev && impPrev.groups.length > 0 &&
                  <button className="gk-kbtn gx-go" onClick={impSave} disabled={impBusy}>{impBusy ? '⏳ …' : `💾 Importieren (${impPrev.parsed} Einträge)`}</button>}
                {impMsg && <span className="gx-frmsg">{impMsg}</span>}
              </div>
              {impPrev && impPrev.groups.length > 0 && (
                <div className="gx-imp-prev">
                  <div className="gx-cllbl">Vorschau — {impPrev.parsed} Änderungen in {impPrev.groups.length} K×Woche:</div>
                  {impPrev.groups.map((g, i) => (
                    <div key={i} className="gx-imp-grp">
                      <strong>{g.campaign} · {g.week_label} ({g.count})</strong>
                      <div className="gx-imp-items">{g.items.slice(0, 4).map((it, j) => <div key={j}>{(it.zeit || '').slice(0, 16)} · {it.text}</div>)}{g.count > 4 && <div>… +{g.count - 4} weitere</div>}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="gx-clrow">
            <label className="gx-cllbl">K-Änderungen · manuell</label>
            <textarea className={'gx-frta' + (clBig.km ? ' gx-frta-big' : '')} rows={clBig.km ? 12 : 3}
              onDoubleClick={() => setClBig(s => ({ ...s, km: !s.km }))}
              placeholder="z. B. Budget bewusst gedeckelt, weil …" value={clData.k_manual}
              onChange={e => setClData(d => ({ ...d, k_manual: e.target.value }))} />
          </div>
          <div className="gx-clrow">
            <label className="gx-cllbl">LP-Änderungen <small>({clData.lp_name})</small></label>
            <textarea className={'gx-frta' + (clBig.lp ? ' gx-frta-big' : '')} rows={clBig.lp ? 12 : 3}
              onDoubleClick={() => setClBig(s => ({ ...s, lp: !s.lp }))}
              placeholder="z. B. Hero-Headline geändert, neuer CTA …" value={clData.lp}
              onChange={e => setClData(d => ({ ...d, lp: e.target.value }))} />
          </div>
        </div>
      </section>

      <div className="gx-actions">
        <label className="gx-clincl" style={{ margin: 0 }}><input type="checkbox" checked={inclJourney} onChange={() => setInclJourney(v => !v)} /> <strong>Journey + Profil</strong> (LP-Wirkung: Sektions-Funnel + Besucher-/Conversion-Profil) ins ZIP</label>
        <button className="gk-kbtn gx-go" onClick={doExport} disabled={busy}>{busy ? '⏳ erstelle ZIP…' : `⬇ ZIP erstellen (${kCamp})`}</button>
        <span className="gk-toolbar-hint">{kCamp} · {picks(selWeeks).length} Wochen · {picks(selR).length} Berichte · {picks(selFr).length} Fragen-Sets{inclCl ? ' · +WasWurdeGemacht' : ''}{inclJourney ? ' · +Journey' : ''}</span>
      </div>
      {err && <div className="gk-err">Fehler: {err}</div>}
      {msg && <div className="gx-ok">{msg}</div>}
      <p className="gk-toolbar-hint" style={{ marginTop: 12 }}>Ein ZIP je Kampagne (oben gewählt): pro Bericht eine CSV + optional <code>{kCamp}_Fragen.csv</code> + <code>{kCamp}_WasWurdeGemacht.csv</code> (Semikolon, dt. Komma, UTF-8).</p>
    </div>
  );
}
